import { splitProps, createMemo } from "solid-js";

/**
 * @typedef {object} UIButtonProps
 * @property {'xs' | 'sm' | 'md' | 'lg'} [size='md'] - Button size
 * @property {'expand' | 'wide' | 'full' } [width='expand'] - Button width
 * @property {'primary' | 'secondary' | 'accent' | 'info' | 'success' | 'warning' | 'error' | 'ghost' | 'link'} [variant] - Button variant
 * @property {import('solid-js').JSX.Element} children - Button content
 * @property {string} [class] - Additional CSS classes
 */

/**
 * A button component with size and variant options
 * @param {UIButtonProps & import('solid-js').JSX.ButtonHTMLAttributes<import('solid-js').JSX.HTMLButtonElement>} props The component props
 * @returns {import('solid-js').JSX.Element} The UIBUtton
 */
export function UIButton(props) {
  const [local, others] = splitProps(props, [
    "size",
    "width",
    "variant",
    "children",
    "className",
  ]);

  const sizeClasses = {
    xs: "tw:d-btn-xs",
    sm: "tw:d-btn-sm",
    md: "tw:d-btn-md",
    lg: "tw:d-btn-lg",
  };

  const textClasses = {
    xs: "tw:text-meta",
    sm: "tw:text-meta",
    md: "tw:text-dense",
    lg: "tw:text-body",
  };

  const widthClasses = {
    expand: "",
    wide: "tw:d-btn-wide",
    full: "tw:d-btn-block",
  };

  const variantClasses = {
    primary: "tw:d-btn-primary",
    secondary: "tw:d-btn-secondary",
    accent: "tw:d-btn-accent",
    info: "tw:d-btn-info",
    success: "tw:d-btn-success",
    warning: "tw:d-btn-warning",
    error: "tw:d-btn-error",
    ghost: "tw:d-btn-ghost",
    link: "tw:d-btn-link",
  };

  const classes = createMemo(() => {
    return [
      "tw:d-btn",
      "tw:d-btn-soft",
      widthClasses[local.width || "expand"],
      sizeClasses[local.size || "md"],
      textClasses[local.size || "md"],
      local.variant ? variantClasses[local.variant] : "",
      local.className || "",
    ]
      .filter(Boolean)
      .join(" ");
  });

  return (
    <button className={classes()} {...others}>
      {local.children}
    </button>
  );
}
